import React from 'react';
import cx from 'classnames';
import { QueueProgress } from './Progress';
import { InfoRow } from '../InfoRow';
import { statusValues } from '../../../constants/productionQueue.constants';
import useSyncAnimations from './hooks/useSynchronizeAnimation';
import useAnimatedQueueRows from './hooks/useAnimatedQueueRows';

const QueueTableBody = ({
  headerOptions = null,
  rows,
  isLoading,
  mini = false,
  stages,
  dataSrcId,
  disabledStatuses = [],
  disableProgressAnimations = false,
  tableAnimation = false,
}) => {
  const { tableRef, registerRow, displayedRows } = useAnimatedQueueRows({
    rows,
    dataSrcId,
    enabled: tableAnimation,
    isLoading,
  });

  useSyncAnimations(displayedRows, tableRef);

  const columns = headerOptions || [];
  const hasMenuColumn = columns.some(column => column.menu);
  const emptyColumnCount = Math.max(
    0,
    columns.filter(column => !column.menu).length - columns.findIndex(column => column.endOfTable) - 2
  );
  const emptyColumns = Array.from({ length: emptyColumnCount }, (_, index) => index);
  let content;

  if (isLoading) {
    content = (
      <InfoRow>
        <i className="fa fa-spinner fa-spin fa-2x" />
      </InfoRow>
    );
  } else if (displayedRows.length === 0) {
    content = (
      <InfoRow>
        <span className="no-info">No records</span>
      </InfoRow>
    );
  } else {
    content = displayedRows.map(row => (
      <tr
        key={row.uuid}
        ref={element => registerRow(row.uuid, element)}
        className={cx(
          'custom-table__tbody-row queue-table__tbody-row',
          {
            cancel: row.isCanceled,
            aperiodic: row.isAperiodic,
            spontaneous: row.isSpontaneous,
            grilled: row.isGrilled,
            wait: row.statusValue === statusValues.WAIT,
            go: row.statusValue === statusValues.GO,
            ['breading-complete']: row.statusValue === statusValues.BREADING_COMPLETE,
            ['disable-animation']: !!row.disableProgressAnimations,
          },
          [row.animation ? row.animation.toLowerCase() : '']
        )}
        style={{ height: row.minHeight || undefined }}
      >
        {row.cells.map((cell, index) => (
          <td
            key={index}
            className={cx('custom-table__body-cell queue-table__body-cell', {
              bordered: cell.endOfTable,
              [cell.className]: !!cell.className,
            })}
          >
            {cell.label || ''}
          </td>
        ))}

        <td key="queue-1" className="queue-table__container">
          <QueueProgress
            row={row}
            mini={mini}
            stages={stages}
            disabledStatuses={disabledStatuses}
            disableProgressAnimations={disableProgressAnimations}
          />

          {row.taskDelay ? (
            <div
              className={cx('queue-table__delay-indicatior', row.taskDelayColor || 'red')}
              style={{
                ...(row.statusValue === statusValues.GO && { right: 'unset', left: 32 }),
                ...(row.statusValue === statusValues.BREADING_COMPLETE && { right: 'unset', left: 120 }),
              }}
            >
              {row.taskDelay}
            </div>
          ) : null}
        </td>

        {emptyColumns.map(index => (
          <td key={`queue-${index + 1}`} />
        ))}

        {hasMenuColumn && row.menuItems?.length > 0 && (
          <td className="queue-table__body-cell">
            <div className="d-flex align-items-center">{row.menuItems.map(item => item.label)}</div>
          </td>
        )}
      </tr>
    ));
  }

  return (
    <table ref={tableRef} className="custom-table__table">
      {headerOptions !== null ? (
        <thead className="custom-table__thead">
          <tr>
            {headerOptions.map((cell, index) => (
              <th
                key={index}
                className={cx('custom-table__header-cell queue-table__header-cell', {
                  bordered: cell.endOfTable,
                  [cell.className]: !!cell.className,
                })}
                style={{
                  width: cell.width || 'auto',
                  minWidth: cell.minWidth || 'auto',
                  maxWidth: cell.maxWidth || 'auto',
                  ...cell.styles,
                }}
              >
                {cell.label}
              </th>
            ))}
          </tr>
        </thead>
      ) : null}

      <tbody className="custom-table__tbody">{content}</tbody>
    </table>
  );
};

export default QueueTableBody;
